import React, { useEffect, useState } from "react";
import PageTitle from "../../../components/PageTitle/PageTitle";
import axios from "../../../utils/axios";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

const FundBeneficiariesList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false); // 🔹 Track add vs edit
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    accountNo: "",
    beneficiaryName: "",
    bankName: null,
    branch: null,
    routingNo: "",
  });

  // Bank & Branch Data
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);

  // 🔹 Fetch Beneficiaries List
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/beneficiaries");
      if (res.data.success) {
        const formatted = res.data.payload.map((item) => ({
          ...item,
          bankName: item.bank?.bankName || "",
          branchDisplay: item.bank
            ? `${item.bank.branchName} - ${item.bank.routingNo}`
            : "",
        }));
        setData(formatted);
      }
    } catch (error) {
      Swal.fire("Error", "Failed to load beneficiaries", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 🔹 Fetch Bank Names when Modal Opens
  useEffect(() => {
    if (open) {
      axios
        .get("/bank/get-all-bank-names")
        .then((res) => {
          if (res.data.success) {
            setBanks(res.data.payload);
          }
        })
        .catch(() => Swal.fire("Error", "Failed to load bank names", "error"));
    }
  }, [open]);

  // 🔹 Fetch Branches when Bank Selected
  const handleBankChange = async (event, value) => {
    setFormData({ ...formData, bankName: value, branch: null, routingNo: "" });
    setBranches([]);
    if (value) {
      try {
        const res = await axios.post("/bank/get-all-banks-by-name", {
          bankName: value.bankName,
        });
        if (res.data.success) {
          setBranches(res.data.payload);
        }
      } catch (error) {
        Swal.fire("Error", "Failed to load branches", "error");
      }
    }
  };

  // 🔹 Handle Branch Change
  const handleBranchChange = (event, value) => {
    setFormData({
      ...formData,
      branch: value,
      routingNo: value ? value.routingNo : "",
    });
  };

  // 🔹 Close Modal + Reset
  const handleClose = () => {
    setOpen(false);
    setEditing(false);
    setFormData({ id: null, name: "", accountNo: "", beneficiaryName: "", bankName: null, branch: null, routingNo: "" });
    setBranches([]);
  };

  // 🔹 Save Beneficiary (POST)
  const handleSave = async () => {
    if (!formData.name || !formData.accountNo || !formData.beneficiaryName || !formData.bankName || !formData.branch) {
      Swal.fire("Warning", "Please fill all fields", "warning");
      return;
    }

    try {
      const res = await axios.post("/beneficiaries", {
        name: formData.name,
        accountNo: formData.accountNo,
        beneficiaryName: formData.beneficiaryName,
        bankId: formData.branch.id, 
        routingNo: formData.routingNo,
        status:0
      });

      if (res.data.success) {
        Swal.fire("Success", "Beneficiary added successfully", "success");
        handleClose();
        fetchData();
      } else {
        Swal.fire("Error", res.data.message || "Failed to add beneficiary", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong!", "error");
    }
  };

  // 🔹 Update Beneficiary (PUT)
  const handleUpdate = async () => {
    if (!formData.id) return;

    try {
      const res = await axios.put(`/beneficiaries/${formData.id}`, {
        name: formData.name,
        accountNo: formData.accountNo,
        beneficiaryName: formData.beneficiaryName,
        bankId: formData.branch?.id,
        routingNo: formData.routingNo,
      });

      if (res.data.success) {
        Swal.fire("Success", "Beneficiary updated successfully", "success");
        handleClose();
        fetchData();
      } else {
        Swal.fire("Error", res.data.message || "Failed to update beneficiary", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong!", "error");
    }
  };

  // 🔹 Edit Button Click
  const handleEdit = (row) => {
    setEditing(true);
    setOpen(true);

    // Prefill form
    setFormData({
      id: row.original.id,
      name: row.original.name,
      accountNo: row.original.accountNo,
      beneficiaryName: row.original.beneficiaryName,
      bankName: { bankName: row.original.bankName }, // Preselect bank
      branch: { id: row.original.bank?.id, branchName: row.original.bank?.branchName, routingNo: row.original.bank?.routingNo },
      routingNo: row.original.bank?.routingNo || "",
    });

    // Load branches for selected bank
    if (row.original.bankName) {
      axios
        .post("/bank/get-all-banks-by-name", { bankName: row.original.bankName })
        .then((res) => {
          if (res.data.success) {
            setBranches(res.data.payload);
          }
        })
        .catch(() => Swal.fire("Error", "Failed to load branches", "error"));
    }
  };

const handleApprove = async (row) => {
  try {
    const res = await axios.put(`/beneficiaries/${row.original.id}`, {
      status: 1,
    });

    if (res.data.success) {
      Swal.fire("Success", "Beneficiary approved successfully", "success");
      fetchData(); // refresh table
    } else {
      Swal.fire("Error", res.data.message || "Failed to approve", "error");
    }
  } catch (error) {
    Swal.fire("Error", "Something went wrong!", "error");
  }
};

const columns = [
  {
    accessorKey: "serialNumber",
    header: "SL",
    size: 50,
    Cell: ({ row }) => row.index + 1,
  },
   { accessorKey: "beneficiaryName", header: "Beneficiary Name", size: 50 },
  { accessorKey: "name", header: "Name", size: 50 },
  { accessorKey: "accountNo", header: "Account No" , size: 50},
 
  { accessorKey: "bankName", header: "Bank Name", size: 50 },
  {
    accessorKey: "branchName",
    header: "Branch Name",
    size: 50,
    Cell: ({ row }) => row.original.bank?.branchName || "",
  },
  {
    accessorKey: "routingNo",
    header: "Routing No",
    size: 50,
    Cell: ({ row }) => row.original.bank?.routingNo || "",
  },
{
  header: "Actions",
  Cell: ({ row }) => {
    const status = row.original.status;

    return (
      <div style={{ display: "flex", gap: "5px" }}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => handleEdit(row)}
          disabled={status === 1}   // 🔹 Disable Edit if already approved
        >
          Edit
        </Button>

        {status === 0 ? (
          <Button
            variant="outlined"
            color="success"
            size="small"
            onClick={() => handleApprove(row)}
          >
            Approve
          </Button>
        ) : (
          <Button
            variant="contained"
            color="default"
            size="small"
            disabled
          >
            Approved
          </Button>
        )}
      </div>
    );
  },
}

];


  return (
    <>




            <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            style={{ marginBottom: "10px" }}
          >
            {/* Left side: title */}
            <Grid item>
              <PageTitle title="Electronic Beneficiary List" />
            </Grid>

            {/* Right side: buttons in one line */}
            <Grid item>
              <Grid container spacing={2}>
                  <Grid item>
                  <Link to="/app/eft/fundBeneficiariesList">
                       <Button variant="contained" color="primary" onClick={() => { setOpen(true); setEditing(false); }}>
               Add Beneficiaries
              </Button>
                  </Link>
                </Grid>
                <Grid item>
                  <Link to="/app/eft/fundTransferRequest">
                    <Button variant="contained" color="primary">
                     Back to Fund Transfer
                    </Button>
                  </Link>
                </Grid>
              
              </Grid>
            </Grid>
          </Grid>


      {/* 🔹 Beneficiaries Table */}
      <MaterialReactTable
        columns={columns}
        data={data}
        state={{ isLoading: loading }}
        enableColumnActions={false}
        enableSorting
        initialState={{ density: "compact" }}
        enablePagination
      />

      {/* 🔹 Add/Edit Beneficiaries Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit Beneficiary" : "Add Beneficiary"}</DialogTitle>
        <DialogContent>
              <TextField
            label="Beneficiary Name"
            fullWidth
            margin="dense"
            value={formData.beneficiaryName}
            onChange={(e) => setFormData({ ...formData, beneficiaryName: e.target.value })}
          />
          <TextField
            label="Acc Holder Name"
            fullWidth
            margin="dense"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            label="Acc No"
            fullWidth
            margin="dense"
            type="number"
            value={formData.accountNo}
            onChange={(e) => setFormData({ ...formData, accountNo: e.target.value })}
          />

          

          {/* 🔹 Bank Dropdown */}
          <Autocomplete
            options={banks}
            getOptionLabel={(option) => option.bankName}
            value={formData.bankName}
            onChange={handleBankChange}
            renderInput={(params) => <TextField {...params} label="Bank Name" margin="dense" fullWidth />}
          />

          {/* 🔹 Branch Dropdown (Branch + Routing No) */}
          <Autocomplete
            options={branches}
            getOptionLabel={(option) =>
              option ? `${option.branchName} - ${option.routingNo}` : ""
            }
            value={formData.branch}
            onChange={handleBranchChange}
            renderInput={(params) => (
              <TextField {...params} label="Branch Name" margin="dense" fullWidth />
            )}
            disabled={!formData.bankName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          {editing ? (
            <Button onClick={handleUpdate} color="primary" variant="contained">
              Update
            </Button>
          ) : (
            <Button onClick={handleSave} color="primary" variant="contained">
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FundBeneficiariesList;
